import { Component, OnInit, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { RouterModule, ActivatedRoute, Router } from '@angular/router';
import { MenuItem } from 'primeng/api';

import { DashboardCampanhaComponent } from './components/dashboard-campanha/dashboard-campanha.component';
import { DashboardDoacaoComponent } from './components/dashboard-doacao/dashboard-doacao.component';
import { PerfilComponent } from '../perfil/perfil.component';
import { UserService } from '@app/auth/services/user.service';
import { AuthService } from '@app/auth/services/auth.service';
import { UtilizadoresService } from '@app/shared/components/api/services/utilizadores.service';
import { PagamentosService } from '@app/shared/components/api/services/pagamentos.service';
import { CampanhaService } from '@app/shared/components/api/services/campanha.service';
import { MoneyPipe } from '@app/shared/pipes/money.pipe';
import { DateMonthPipe } from '@app/shared/pipes/date-month.pipe';

import { Drawer } from 'primeng/drawer';
import { DetalhePagamentoComponent } from '@app/features-modules/admin/components/pagamentos/components/detalhe-pagamento/detalhe-pagamento.component';

@Component({
  selector: 'app-dashboards',
  standalone: true,
  imports: [
    CommonModule,
    RouterModule,
    MoneyPipe,
    DateMonthPipe,
    DashboardCampanhaComponent,
    DashboardDoacaoComponent,
    PerfilComponent,
    Drawer,
    DetalhePagamentoComponent,
  ],
  providers: [UtilizadoresService, PagamentosService, CampanhaService],
  templateUrl: './dashboards.component.html',
  styleUrl: './dashboards.component.scss',
})
export class DashboardsComponent implements OnInit {
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  readonly userService = inject(UserService);
  readonly authService = inject(AuthService);
  private readonly utilizadoresService = inject(UtilizadoresService);
  private readonly pagamentosService = inject(PagamentosService);
  private readonly campanhaService = inject(CampanhaService);

  // Active section: 0 = overview, 1 = campanhas investidas (doações), 2 = as minhas campanhas (criador), 3 = perfil
  selectedSection: number = 0;

  // Mobile sidebar open state
  sidebarOpen: boolean = false;

  // User profile
  utilizador: any = null;
  userName: string = 'Utilizador';
  userEmail: string = '';
  userInitials: string = 'U';

  // Overview KPIs & data
  totalInvestido: number = 0;
  totalCampanhasApoiadas: number = 0;
  totalCampanhasConcluidas: number = 0;
  totalCampanhasCriadas: number = 0;
  recentActivities: any[] = [];
  loadingOverview: boolean = false;

  // Drawer de detalhes de pagamento/transação (Actividade Recente)
  showDetalhesPagamento: boolean = false;
  pagamentoID: any = null;

  ngOnInit(): void {
    const uid = this.userService.getUserId();
    this.userName = this.userService.getUsername() || 'Investidor';
    this.userEmail = this.userService.getUserEmail() || '';
    this.userInitials = this.userService.getUserInitials() || 'FC';

    if (uid) {
      this.loadUserProfile(uid);
      this.loadOverviewData(uid);
    }

    this.route.queryParams.subscribe((params) => {
      if (params['tab'] === 'perfil') {
        this.selectedSection = 3;
      } else if (
        params['tab'] === 'criador' ||
        params['tab'] === 'minhas-campanhas'
      ) {
        this.selectedSection = 2;
      } else if (params['tab'] === 'doacao' || params['tab'] === 'investidas') {
        this.selectedSection = 1;
      } else if (params['tab'] === 'overview') {
        this.selectedSection = 0;
      }
    });
  }

  loadUserProfile(id: string) {
    this.utilizadoresService.show(id).subscribe({
      next: (res) => {
        if (res?.data) {
          this.utilizador = res.data;
          if (this.utilizador.nomeCompleto) {
            this.userName = this.utilizador.nomeCompleto;
          }
          if (this.utilizador.email) {
            this.userEmail = this.utilizador.email;
          }
        }
      },
      error: (err) => console.error('Erro ao obter perfil', err),
    });
  }

  loadOverviewData(userId: string) {
    this.loadingOverview = true;

    // Load recent payments / donations by user
    this.pagamentosService
      .list({
        UtilizadorID: userId,
        PageSize: 5,
        PageNumber: 1,
        OrderBy: 'id desc',
      })
      .subscribe({
        next: (res) => {
          const list = res?.data ?? [];
          this.recentActivities = list;
          this.totalCampanhasApoiadas = res?.totalRecords || list.length;

          // Sum contributions
          let sum = 0;
          list.forEach((item: any) => {
            sum += Number(
              item.montante || item.valor || item.apoio?.valorApoio || 0,
            );
          });
          this.totalInvestido = sum;
          this.loadingOverview = false;
        },
        error: () => {
          this.loadingOverview = false;
        },
      });

    // Load user campaigns count
    this.campanhaService.list({ UtilizadorID: userId, PageSize: 1 }).subscribe({
      next: (res) => {
        this.totalCampanhasCriadas = res?.totalRecords || 0;
      },
    });
  }

  showSection(index: number) {
    this.selectedSection = index;
    this.closeSidebar();

    const tabMap: Record<number, string> = {
      0: 'overview',
      1: 'investidas',
      2: 'criador',
      3: 'perfil',
    };
    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: { tab: tabMap[index] },
      queryParamsHandling: 'merge',
    });
  }

  toggleSidebar() {
    this.sidebarOpen = !this.sidebarOpen;
  }

  closeSidebar() {
    this.sidebarOpen = false;
  }

  confirmLogout(event: Event) {
    event.preventDefault();
    this.authService.logout();
    this.router.navigate(['/']);
  }

  getPercent(item: any): number {
    const meta =
      item?.apoio?.campanha?.metaArrecadar || item?.campanha?.metaArrecadar;
    const arrecadado =
      item?.apoio?.campanha?.valorArrecado || item?.campanha?.valorArrecado;
    if (!meta || meta <= 0) return 0;
    return Math.round(((arrecadado || 0) / meta) * 100);
  }

  abrirDetalhesActividade(act: any): void {
    const id = act?.apoio?.id || act?.apoioID || act?.id;
    if (id) {
      this.pagamentoID = id;
      this.showDetalhesPagamento = true;
    }
  }

  fecharDetalhesPagamento(): void {
    this.showDetalhesPagamento = false;
    this.pagamentoID = null;
  }
}
